import { useMemo, useRef } from 'react'
import { useStableLiveQuery } from '../../../../../lib/db/useStableLiveQuery'
import { useActivePausedLiveQuery } from '../../../../../lib/db/useActivePausedLiveQuery'
import { useStableEntityArray, baseEntityIdentity } from '../../../../../lib/db/useStableEntityArray'
import { db } from '../../../../../lib/db/indexeddb'
import type { Task } from '../../../../../lib/db/models'
import { useOptimisticTaskOverrides } from '../../../hooks/useOptimisticTaskOverrides'
import {
  addMonths,
  buildMonthBuckets,
  filterUpcomingTasks,
  startOfMonth,
  type MonthBucket,
} from './upcomingTasks.utils'

interface UseUpcomingTasksArgs {
  lifeSpace: 'personal' | 'work' | 'home'
  startMonth: Date
  endMonth: Date
  graceIds: Set<string>
  /** True when the containing page is the active tab. While false, the
   *  tasks subscription is paused so unrelated writes don't re-fire the
   *  range scan on an invisible UpcomingPage. */
  isActive: boolean
}

/**
 * Live-queries tasks + projects, filters to the given life space, and builds
 * `MonthBucket[]` for the requested range. Re-computes on Dexie writes and
 * whenever `graceIds` changes (so just-completed tasks disappear after their
 * grace window).
 *
 * Query strategy: two index-scoped range queries on `startDate` and
 * `dueDate` (both indexed in Dexie v15), merged + deduped. Tasks outside
 * the rendered month range are never pulled into memory — previously this
 * was a full-table scan that re-ran on every unrelated Dexie write.
 */
export function useUpcomingTasks({
  lifeSpace,
  startMonth,
  endMonth,
  graceIds,
  isActive,
}: UseUpcomingTasksArgs): {
  months: MonthBucket[]
  isLoading: boolean
  /** Optimistically apply a partial task update so the change is visible
   *  in the next paint, instead of waiting on `db.updateTask` →
   *  LiveQuery → re-render (~1–2s with sync cycles in flight). Used for
   *  drag-and-drop (date change) AND badge updates (attention, duration,
   *  tags, etc.). The override is auto-cleared key-by-key as soon as the
   *  underlying Dexie data — checked BEFORE the override is re-applied —
   *  matches every key in the partial. */
  setOptimisticTaskOverride: (taskId: string, partial: Partial<Task>) => void
} {
  const projects = useStableLiveQuery(
    () => db.projects
      .where('lifeSpace')
      .equals(lifeSpace)
      .filter(p => !p.deletedAt)
      .toArray(),
    [lifeSpace],
  )

  const lifeSpaceProjectIds = useMemo(
    () => new Set((projects ?? []).map(p => p.id)),
    [projects],
  )

  // Inclusive upper bound = first day of the month AFTER endMonth, so
  // `.below()` captures every day of endMonth itself.
  const rangeStart = startOfMonth(startMonth)
  const rangeEnd = addMonths(startOfMonth(endMonth), 1)
  const rangeStartMs = rangeStart.getTime()
  const rangeEndMs = rangeEnd.getTime()

  // Tasks whose `startDate` falls in the visible range.
  const tasksByStartRaw = useActivePausedLiveQuery(
    () => db.tasks
      .where('startDate').between(rangeStart, rangeEnd, true, false)
      .filter(t => !t.deletedAt)
      .toArray(),
    [rangeStartMs, rangeEndMs],
    isActive,
  )

  // Tasks with only a `dueDate` in range — filtered to `startDate == null`
  // so we don't double-count tasks already returned by the startDate query.
  const tasksByDueOnlyRaw = useActivePausedLiveQuery(
    () => db.tasks
      .where('dueDate').between(rangeStart, rangeEnd, true, false)
      .filter(t => !t.deletedAt && t.startDate == null)
      .toArray(),
    [rangeStartMs, rangeEndMs],
    isActive,
  )

  // Stabilize task references by `id|updatedAt`. Dexie returns fresh Task
  // objects on every subscription firing (including the catch-up query on
  // tab re-activate), which otherwise breaks the memo chain below and
  // forces a full 3-month re-render even when nothing changed.
  const tasksByStart = useStableEntityArray(tasksByStartRaw, baseEntityIdentity)
  const tasksByDueOnly = useStableEntityArray(tasksByDueOnlyRaw, baseEntityIdentity)

  // Raw merged tasks straight from Dexie — does NOT incorporate optimistic
  // overrides. Drives the override-cleanup heuristics in the shared hook
  // below (compared against raw values, not against override-applied
  // values, so cleanup never snaps the UI back to its old state mid-flight).
  const rawTasks: Task[] | undefined = useMemo(() => {
    if (tasksByStart === undefined || tasksByDueOnly === undefined) return undefined
    return [...tasksByStart, ...tasksByDueOnly]
  }, [tasksByStart, tasksByDueOnly])

  // Optimistic per-task partial overrides via the shared hook (object
  // deep-equal cleanup, hard timeout, updatedAt-advance early-clear).
  const { optimisticTaskOverrides, setOptimisticTaskOverride } =
    useOptimisticTaskOverrides(rawTasks)

  const tasks: Task[] | undefined = useMemo(() => {
    if (rawTasks === undefined) return undefined
    if (optimisticTaskOverrides.size === 0) return rawTasks
    return rawTasks.map(t => {
      const override = optimisticTaskOverrides.get(t.id)
      if (!override) return t
      return { ...t, ...override }
    })
  }, [rawTasks, optimisticTaskOverrides])

  // Hold the previous bucket result so buildMonthBuckets can ref-reuse
  // unchanged days/months. Without this, every Dexie write spawns fresh
  // refs all the way down → MonthBlock/DayGroup/TaskEntry memos all miss
  // → 2-second cascade re-render on every task mutation.
  const prevBucketsRef = useRef<MonthBucket[]>([])
  const months = useMemo(() => {
    if (!tasks || !projects) return prevBucketsRef.current
    const filtered = filterUpcomingTasks({
      tasks,
      lifeSpace,
      lifeSpaceProjectIds,
      graceIds,
    })
    const next = buildMonthBuckets(filtered, startMonth, endMonth, prevBucketsRef.current)
    prevBucketsRef.current = next
    return next
  }, [tasks, projects, lifeSpace, lifeSpaceProjectIds, graceIds, startMonth, endMonth])

  return {
    months,
    isLoading: tasks === undefined || projects === undefined,
    setOptimisticTaskOverride,
  }
}
